import { midnightMarketsByChain } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { midnightCollateralRef } from '@1delta/data-sdk'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { midnightLenderKey } from '../../public-data/midnight/convertPublic'
import { midnightMarketCallCount } from './userCallBuild'

/**
 * Midnight credit/debt units are scaled to the LOAN TOKEN's decimals (verified
 * against the live API: `assets = units * price / WAD`, so units and assets
 * share the loan token's decimal scale — e.g. 6-dec for a USDC market, 18-dec
 * for a WETH market). Debt (face value at maturity) is therefore formatted with
 * the market's `loanDecimals`, not a fixed 18.
 */
function toBigInt(v: any): bigint {
  if (v === undefined || v === null || v === '0x') return 0n
  if (typeof v === 'bigint') return v
  try {
    return BigInt(v as any)
  } catch {
    return 0n
  }
}

// `position(id,user)` decodes to a 6-tuple:
// [credit, pendingFee, lastLossFactor, lastAccrual, debt, collateralBitmap].
function readDebtUnits(positionResult: any): bigint {
  if (Array.isArray(positionResult)) return toBigInt(positionResult[4])
  if (positionResult && typeof positionResult === 'object')
    return toBigInt(positionResult.debt)
  return 0n
}

// `updatePositionView(market,id,user)` decodes to the up-to-date lender state
// [newCredit, newPendingFee, accruedFee]. Net redeemable face value (the
// lender's supply position) = newCredit − newPendingFee; the raw `position`
// credit/pendingFee are stale (fee accrual + loss slashing happen only here).
function readUpdatedLender(updateResult: any): {
  credit: bigint
  pendingFee: bigint
} {
  if (Array.isArray(updateResult)) {
    return {
      credit: toBigInt(updateResult[0]),
      pendingFee: toBigInt(updateResult[1]),
    }
  }
  if (updateResult && typeof updateResult === 'object') {
    return {
      credit: toBigInt(updateResult.newCredit),
      pendingFee: toBigInt(updateResult.newPendingFee),
    }
  }
  return { credit: 0n, pendingFee: 0n }
}

/**
 * Returns a [converter, expectedCallCount] tuple for Midnight user data.
 *
 * Layout mirrors `buildMidnightUserCall`: per configured market, a `position`
 * result, then an `updatePositionView` result, then one `collateral(index)`
 * result per collateral leg. Emits one `UserData` per market key
 * `MORPHO_MIDNIGHT_<id>` (multi-market), each with a single sub-account holding
 * the loan side (borrower debt + lender net supply) + collateral legs.
 *
 * NOTE: debt AND lender credit are reported at face value (units settle 1:1 at
 * maturity), not discounted present value. The lender's net supply is
 * credit − pendingFee (continuous-fee haircut). Health / borrow-capacity are
 * computed downstream from LLTVs + oracle prices in
 * `createMultiAccountTypeUserState`.
 */
export const getMidnightUserDataConverter = (
  _lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const markets = midnightMarketsByChain(chainId)
  const totalCalls = markets.reduce(
    (sum, m) => sum + midnightMarketCallCount(m.collateralParams.length),
    0,
  )

  return [
    (data: any[]) => {
      if (!data || data.length < totalCalls) return undefined

      const out: { [lender: string]: UserData } = {}
      let cursor = 0

      for (const market of markets) {
        const positionResult = data[cursor]
        const updateResult = data[cursor + 1]
        const collateralResults = market.collateralParams.map(
          (_c, i) => data[cursor + 2 + i],
        )
        cursor += midnightMarketCallCount(market.collateralParams.length)

        const lenderKey = midnightLenderKey(market.marketId)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) continue

        const loanAddr = market.loanToken.toLowerCase()
        const loanUid = createMarketUid(chainId, lenderKey, loanAddr)
        const loanMeta = metaMap[loanUid]
        const loanDisplayPrice = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOraclePrice = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanPriceHist =
          loanMeta?.price?.priceUsd24h ?? loanDisplayPrice

        const debtUnits = readDebtUnits(positionResult)
        const debtStr = parseRawAmount(debtUnits.toString(), market.loanDecimals)
        const debtNum = Number(debtStr)

        // Lender (supply) side: up-to-date net redeemable face value =
        // credit − pendingFee, both in loan-token units. `pendingFee` is the
        // accrued continuous-fee haircut already deducted from `deposits`.
        const { credit, pendingFee } = readUpdatedLender(updateResult)
        const netCreditUnits = credit > pendingFee ? credit - pendingFee : 0n
        const depositsStr = parseRawAmount(
          netCreditUnits.toString(),
          market.loanDecimals,
        )
        const depositsNum = Number(depositsStr)
        const pendingFeeStr = parseRawAmount(
          pendingFee.toString(),
          market.loanDecimals,
        )

        const posData: Record<string, any> = {}
        let anyBalance = debtUnits > 0n || netCreditUnits > 0n
        let depositsUSD24h = depositsNum * loanPriceHist

        // Loan side: borrower debt AND/OR lender supply on the same market.
        posData[loanUid] = {
          marketUid: loanUid,
          underlying: loanAddr,
          deposits: depositsStr,
          debt: debtStr,
          debtStable: '0',
          depositsUSD: depositsNum * loanDisplayPrice,
          debtUSD: debtNum * loanDisplayPrice,
          debtStableUSD: 0,
          depositsUSDOracle: depositsNum * loanOraclePrice,
          debtUSDOracle: debtNum * loanOraclePrice,
          debtStableUSDOracle: 0,
          stableBorrowRate: '0',
          // Supply on Midnight is a lend position, not collateral for a borrow.
          collateralEnabled: false,
          claimableRewards: 0,
          // Midnight-specific: accrued continuous fee owed by the lender (loan
          // units), already netted out of `deposits`. 0 on markets with no
          // continuous fee (the current live default).
          pendingFee: pendingFeeStr,
        }

        // Collateral legs
        market.collateralParams.forEach((c, i) => {
          const collAmt = toBigInt(collateralResults[i])
          if (collAmt === 0n) return
          anyBalance = true
          const collAddr = c.token.toLowerCase()
          // Leg-keyed (see `midnightCollateralRef`). Keyed by ASSET this
          // overwrote the loan entry whenever a leg's token IS the loan token,
          // and the loan entry is where DEBT lives — so a borrower in such a
          // market read as debt-free, with a healthy-looking position.
          const collUid = createMarketUid(
            chainId,
            lenderKey,
            midnightCollateralRef(collAddr, i),
          )
          const collMeta = metaMap[collUid]
          const collDec = collMeta?.asset?.decimals ?? c.decimals
          const collDisplayPrice = collMeta ? getDisplayPrice(collMeta) : 0
          const collOraclePrice = collMeta ? getOraclePrice(collMeta) : 0
          const collPriceHist = collMeta?.price?.priceUsd24h ?? collDisplayPrice
          const depositsStr = parseRawAmount(collAmt.toString(), collDec)
          const depositsNum = Number(depositsStr)
          depositsUSD24h += depositsNum * collPriceHist

          posData[collUid] = {
            marketUid: collUid,
            underlying: collAddr,
            deposits: depositsStr,
            debt: '0',
            debtStable: '0',
            depositsUSD: depositsNum * collDisplayPrice,
            debtUSD: 0,
            debtStableUSD: 0,
            depositsUSDOracle: depositsNum * collOraclePrice,
            debtUSDOracle: 0,
            debtStableUSDOracle: 0,
            stableBorrowRate: '0',
            collateralEnabled: true,
            claimableRewards: 0,
          }
        })

        if (!anyBalance) continue

        const userData = createMultiAccountTypeUserState(
          {
            chainId,
            account,
            lendingPositions: { '0': posData },
            modes: { '0': 0 },
          } as any,
          metaMap,
          { '0': { totalDeposits24h: depositsUSD24h, totalDebt24h: debtNum * loanPriceHist } },
        )

        if (userData && userData.data.length > 0) out[lenderKey] = userData
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    totalCalls,
  ]
}
